package com.downdogapp.client.controllers

import com.downdogapp.client.MANIFEST
import com.downdogapp.client.SequenceSettings
import com.downdogapp.client.ViewController
import com.downdogapp.client.api.*
import com.downdogapp.client.singleton.App
import com.downdogapp.client.singleton.Key
import com.downdogapp.client.singleton.UserPrefs
import com.downdogapp.client.views.MixView
import kotlin.math.roundToInt

class MixViewController(val selectorItem: SettingSelectorItem,
                        val onboardingScreenIndex: Int? = null,
                        val numOnboardingScreens: Int? = null,
                        val onFinishFn: () -> Unit = {}) : ViewController() {

  private val mixConfig get() = SequenceSettings.getMixConfig()!!

  val mixGroups
    get() = MANIFEST.mixGroups.filter { it.id in mixConfig.groupIds }

  val mixPresets
    get() = MANIFEST.mixPresets.filter { it.id in mixConfig.presetIds }

  val isOnboarding get() = onboardingScreenIndex != null

  private var expandedGroup: MixGroup? = null

  override val view = MixView(this)

  init {
    check((onboardingScreenIndex == null) == (numOnboardingScreens == null))
    view.refreshView()
  }

  fun isSubgroupEnabled(subgroup: MixSubgroup) = SequenceSettings.getMixSubgroupEnabled(subgroup.id)

  fun getGroupAmount(group: MixGroup) = getGroupAmount(group.id)

  private fun getGroupAmount(groupId: Int) =
    (SequenceSettings.getMixGroupAmount(groupId).toDouble() / 100 * MANIFEST.mixGroupNumIncrements)
      .roundToInt() * 100 / MANIFEST.mixGroupNumIncrements

  fun getSubgroupsForGroup(group: MixGroup) =
    mixConfig
      .subgroupIds
      .mapNotNull { SequenceSettings.getMixSubgroup(it) }
      .filter { it.groupId == group.id }

  fun hasSubgroups(group: MixGroup) = getSubgroupsForGroup(group).isNotEmpty()

  fun isOnlyEnabled(group: MixGroup) = SequenceSettings.getMixOnlyGroupId(mixConfig) == group.id

  fun isDisabled(group: MixGroup) =
    getGroupAmount(group.id) == 0
    || SequenceSettings.getMixOnlyGroupId(mixConfig)?.let { it != group.id } ?: false

  fun isExpanded(group: MixGroup) = expandedGroup == group

  fun isSelected(preset: MixPreset) =
    SequenceSettings.getSelectedMixPreset(mixConfig)?.id == preset.id

  fun setGroupAmount(group: MixGroup, amount: Int, only: Boolean) {
    if (amount == 0 && mixConfig.groupIds.all { it == group.id || getGroupAmount(it) == 0 }) {
      return
    }
    SequenceSettings.setMixGroupAmount(group.id, amount)
    SequenceSettings.setMixOnlyGroup(if (only) group.id else null)
    logEvent(
      "set_group_amount",
      mapOf("group_id" to group.id.toString(), "amount" to amount.toString(), "only" to only.toString())
    )
    view.refreshView()
  }

  fun toggleSubgroupEnabled(subgroup: MixSubgroup) {
    val groupSubgroups =
      MANIFEST
        .mixSubgroups
        .filter { it.id in mixConfig.subgroupIds && it.groupId == subgroup.groupId }
    if (groupSubgroups.all { !isSubgroupEnabled(it) xor (it == subgroup) }) {
      return // Disallow the all disabled case
    }
    SequenceSettings.setMixSubgroupEnabled(subgroup.id, !isSubgroupEnabled(subgroup))
    logEvent(
      "toggle_subgroup",
      mapOf(
        "subgroupId" to subgroup.id.toString(),
        "enabled" to (!(UserPrefs[Key.MixSubgroupEnabled(subgroup.id)] ?: false)).toString()
      )
    )
    view.refreshView()
  }

  fun toggleExpanded(group: MixGroup) {
    if (hasSubgroups(group)) {
      expandedGroup = if (isExpanded(group)) null else group
      view.refreshView()
    }
  }

  fun setSelectedPreset(preset: MixPreset) {
    if (!isSelected(preset)) {
      expandedGroup = null
      SequenceSettings.setSelectedMixPreset(preset)
      view.refreshView()
    }
  }

  fun onFinish() {
    SequenceSettings.resetCacheAndMaybeFetchSequences()
    onFinishFn()
    if (!isOnboarding) {
      SequenceSettings.saveSelectorToRecents(MANIFEST.mixPresetTypeId)
      App.popViewController() // For multi-page onboarding, stack view controllers, don't pop them
    }
  }

  override fun onBackClicked() {
    if (isOnboarding) {
      App.popViewController()
    } else {
      onFinish()
    }
  }
}
